// Simple in-memory cache for API requests to prevent duplicate calls
import { log } from '@/lib/utils/logger';

interface CacheEntry {
  data: unknown;
  timestamp: number;
}

const apiCache = new Map<string, CacheEntry>();
const apiRequestCache = new Map<string, Promise<unknown>>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function getCachedData(key: string): unknown {
  const cached = apiCache.get(key);
  
  if (cached) {
    const now = Date.now();
    if (now - cached.timestamp < CACHE_DURATION) {
      log.debug(`Cache Hit: ${key}`);
      return cached.data;
    } else {
      // Cache expired
      apiCache.delete(key);
    }
  }
  
  return null;
}

export function setCachedData(key: string, data: unknown): void {
  apiCache.set(key, { data, timestamp: Date.now() });
  log.debug(`Cache Set: ${key}`);
}

export function getCachedRequest(key: string): Promise<unknown> | null {
  return apiRequestCache.get(key) || null;
}

export function setCachedRequest(key: string, promise: Promise<unknown>): void {
  apiRequestCache.set(key, promise);
  log.debug(`Request Cache Set: ${key}`);
}

export function clearRequestCache(key: string): void {
  apiRequestCache.delete(key);
}

export function clearCache(key?: string): void {
  if (key) {
    apiCache.delete(key);
    apiRequestCache.delete(key);
  } else {
    apiCache.clear();
    apiRequestCache.clear();
  }
}
